import { makeAutoObservable } from 'mobx'
import { type IResponseNumberLandline } from 'entities/Phone/api/types'
import { type Phone } from 'entities/Phone/model/Phone'

export class PhoneLandline {
  private _id = 0
  private _completed = 0
  private _status: IResponseNumberLandline['status'] | null = null
  private _landline_name: IResponseNumberLandline['landline_name'] = ''
  private _city: IResponseNumberLandline['city'] = ''
  private _street_address: IResponseNumberLandline['street_address'] = ''
  private _state: IResponseNumberLandline['state'] = ''
  private _zip_code: IResponseNumberLandline['zip_code'] = ''
  private _can_verify: IResponseNumberLandline['can_verify'] = false

  constructor(response: IResponseNumberLandline, private _phone: Phone) {
    makeAutoObservable(this)

    this.syncOrigin(response)
  }

  syncOrigin = (origin: IResponseNumberLandline) => {
    this._id = origin.id
    this._completed = origin.completed
    this._status = origin.status
    this._landline_name = origin.landline_name
    this._city = origin.city
    this._street_address = origin.street_address
    this._state = origin.state
    this._zip_code = origin.zip_code
    this._can_verify = origin.can_verify
  }

  updateStatus = (status: IResponseNumberLandline['status']) => {
    this._status = status
  }

  get can_verify() {
    return this._can_verify
  }

  get zip_code() {
    return this._zip_code
  }

  get state() {
    return this._state
  }

  get street_address() {
    return this._street_address
  }

  get city() {
    return this._city
  }

  get id() {
    return this._id
  }

  get landlineName() {
    return this._landline_name
  }

  get isCompleted() {
    return Boolean(this._completed)
  }

  get status() {
    return this._status
  }

  get statusTwilioProcessing() {
    return this._status === 'twilio-processing' // 2 - status delete - false
  }

  get statusReceived() {
    return this._status === 'received' // 3 - status delete - true
  }

  get statusPendingVerification() {
    return this._status === 'pending-verification' // 3 - status delete - true
  }

  get statusVerified() {
    return this._status === 'verified' // 4 - status delete - true
  }

  get statusPendingLoa() {
    return this._status === 'pending-loa' // 4 - 30sec fetch - status delete - false
  }

  get statusCarrierProcessing() {
    return this._status === 'carrier-processing' // 4 - success - status delete - false
  }

  get statusTesting() {
    return this._status === 'testing' // 4 - status delete - false
  }

  get statusCompleted() {
    return this._status === 'completed' // 5 - status delete - false
  }

  get statusMigration() {
    return Boolean(this._phone.numberable) // 7 - (Zipwhip)
  }

  get statusActionRequired() {
    return this._status === 'action-required' // - status delete - false
  }

  get statusFailed() {
    return this._status === 'failed' // status delete - false
  }

  get statusCanceled() {
    return this._status === 'canceled'
  }

  get disabledStatus() {
    return (
      this.statusTwilioProcessing ||
      this.statusPendingLoa ||
      this.statusCarrierProcessing ||
      this.statusTesting ||
      this.statusCompleted ||
      this.statusActionRequired ||
      this.statusFailed
    )
  }

  get hasVerify() {
    return this.statusReceived || this.statusPendingVerification
  }

  get hasLOA() {
    return this.statusPendingLoa || this.statusCarrierProcessing || this.statusTesting
  }

  get hasVerified() {
    return this.statusVerified
  }

  get hasCompleted() {
    if (this._phone.is_zipwhip) {
      return this.statusCompleted && !this.statusMigration
    }

    return this.statusCompleted
  }

  get hasIncomplete() {
    return (
      this.statusTwilioProcessing ||
      this.statusReceived ||
      this.statusPendingVerification ||
      this.statusVerified ||
      this.statusCanceled
    )
  }
}
